import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import toastr from 'react-hot-toast';
import { INFINITE_CACHE_AND_STALE_TIME } from 'constants/index';
import { generateURL } from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import type { DropdownOption } from 'types/common/DropdownOption';
import type {
  FlagReasonResponse,
  FlagReason,
} from 'types/entityTypes/FlagReason';
import { useCurrentUser } from 'hooks/common';

const getFlagReasons = () => {
  const url = generateURL(SPARK_RESOURCE_TYPES.FLAG_REASONS);
  return fetchWrapper<FlagReasonResponse>({
    url: url,
    method: 'GET',
  });
};

export function useFlagReasons() {
  const { currentUserID } = useCurrentUser();
  const { data, ...rest } = useQuery('flagReasons', getFlagReasons, {
    onError: (error) => {
      if (error instanceof Error) {
        toastr.error(`failed to Fetch Flag Reasons: ${error.message}`);
      }
    },
    enabled: !!currentUserID,
    ...INFINITE_CACHE_AND_STALE_TIME,
  });

  const { data: responseData, ...restData } = data || {};

  const idMap = responseData?.reduce<Record<string, FlagReason>>(
    (acc, curr) => {
      return { ...acc, [curr.id]: curr };
    },
    {}
  );

  const sortedFlagReasons = responseData?.sort((a, b) => {
    const sparkID_A = a.attributes.sortOrder;
    const sparkID_B = b.attributes.sortOrder;

    if (sparkID_A < sparkID_B) {
      return -1;
    } else if (sparkID_A > sparkID_B) {
      return 1;
    }
    return 0;
  });

  const dropdownOptions: DropdownOption[] =
    sortedFlagReasons?.map((type) => ({
      label: type.attributes.name,
      value: type.id,
      description: type.attributes.description,
    })) || [];

  return {
    flagReasons: sortedFlagReasons || [],
    idMap,
    dropdownOptions,
    ...restData,
    ...rest,
  };
}
